import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import ClockIcon from '@src/svgs/ClockIcon';
import {
  getAiDescriptionResponse,
  getArticlesCount,
  getDashboardStoryDurationLabel,
  getPullQuotesCount,
  getSocialPostsCount,
  parseClipThemeTitles,
  resolveDashboardStoryThumbnailUrl,
} from '@src/utility/story';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { deriveUploadStep } from '../uploadStatus';
import useUploadPhase from '../user-story/useUploadPhase';
import {
  hasUploadFailed,
  isProcessingFailure,
  isStoryAbandoned,
  isStoryEditorEntryReady,
  isStoryLoading,
  isStoryTranscriptionReady,
  isUploadFailure,
} from '../utils';
import { getAllClips, getVisibleClips } from './clipDisplay';
import ContentCell from './ContentCell';
import { getContentItems } from './contentSummary';
import { setStoryIdsDragPayload } from './dragPayload';
import { setStoriesDragImage } from './dragPreview';
import { draggingSource } from './dragStyles';
import { selectedRowIndicator } from './selectionStyles';
import { getStoryLastActivityMs } from './storyActivity';
import StoryClipRow from './StoryClipRow';
import {
  useFolderViewActions,
  useStorySelection,
} from './storyFolderViewContext';
import StoryRowActionsMenu from './StoryRowActionsMenu';
import StoryTopicsCell from './StoryTopicsCell';

import type { DashboardStory, OtherVideo } from '@src/types.ts/story';
import type { DragEvent, FC, MouseEvent } from 'react';

type Props = {
  story: DashboardStory;
  /** Whether this row is the currently highlighted (single-clicked) story. */
  isSelected?: boolean;
  /** Called on single-click — highlights the row without navigating. */
  onSelect?: (story: DashboardStory) => void;
  onOpen: (story: DashboardStory) => void;
  onRename: (story: DashboardStory) => void;
  onDelete: (story: DashboardStory) => void;
  /** Discard a failed/incomplete upload and reopen the upload modal to try again. */
  onRetry: (story: DashboardStory) => void;
  /** Remove a failed/incomplete upload from the list (no confirmation). */
  onRemoveFailed: (story: DashboardStory) => void;
  /** Whether the story's clips are expanded beneath it. */
  isExpanded?: boolean;
  /** Set this story's clip disclosure (true = expand, false = collapse). */
  onToggleExpand?: (storyId: string, expanded: boolean) => void;
  /** Currently highlighted clip id (single-click). */
  selectedClipId?: string | null;
  /** Single-click a clip — highlights it. */
  onSelectClip?: (clip: OtherVideo) => void;
  /** Double-click a clip — opens its preview. */
  onOpenClip?: (clip: OtherVideo) => void;
  /** Open the Details panel for this story. */
  onDetails?: (story: DashboardStory) => void;
  /** Open the Details panel for one of this story's clips. */
  onClipDetails?: (clip: OtherVideo) => void;
};

const UnfolderedStoryRow: FC<Props> = observer(
  ({
    story,
    isSelected: isRowSelected,
    onSelect,
    onOpen,
    onRename,
    onDelete,
    onRetry,
    onRemoveFailed,
    isExpanded,
    onToggleExpand,
    selectedClipId,
    onSelectClip,
    onOpenClip,
    onDetails,
    onClipDetails,
  }) => {
    const selection = useStorySelection();
    const actions = useFolderViewActions();
    const isBulkSelected = selection.isSelected(story.id);
    const [isDragging, setIsDragging] = useState(false);

    // Phase-based upload/processing status (mirrors the recent view so the
    // folder view shows the same in-progress line item while a story uploads).
    const { phase, percent, clientVideoAnalysisProgress } =
      useUploadPhase(story);
    const isAnalyzingVideo = clientVideoAnalysisProgress !== undefined;
    const stepInfo = deriveUploadStep(
      story,
      phase,
      clientVideoAnalysisProgress,
    );
    const isFailedOrAbandoned =
      (isStoryLoading(story) &&
        (isStoryAbandoned(story) || isUploadFailure(story))) ||
      hasUploadFailed(story);
    const isLoading = isStoryLoading(story) && !isFailedOrAbandoned;
    // PLAT-11224: the row turns interactive at transcription-ready
    // (combine_results) — video renditions keep processing in the background.
    const canEnterEditor =
      isStoryEditorEntryReady(story, isAnalyzingVideo) && !isFailedOrAbandoned;
    const displayPercent = Math.round(percent ?? story.completionPercent ?? 0);

    const thumbnail = resolveDashboardStoryThumbnailUrl(story);
    const duration = getDashboardStoryDurationLabel(story);
    const clips = getAllClips(story);
    const clipCount = clips.length;
    // Published = has a rendered file, directly or on a variant. Deliberately
    // the same predicate the clip rows' badges use, so this rollup and the green
    // badges under an expanded row always agree on the number.
    const publishedClipCount = getVisibleClips(story).length;
    const hasClips = clips.length > 0;
    // "Last activity" — folds the clips in, so editing a clip moves the row's
    // date (the story record alone would not). See `storyActivity.ts`.
    const activityMs = getStoryLastActivityMs(story);
    const date = activityMs
      ? new Date(activityMs).toLocaleDateString()
      : undefined;
    const topicTitles = parseClipThemeTitles(story.clipThemesJson);
    const pullQuotesCount = getPullQuotesCount(story);
    const articlesCount = getArticlesCount(story);
    const socialPostsCount = getSocialPostsCount(story);
    // AI-generated summary surfaced beneath the title as secondary context for
    // scanning the list; empty for stories without a generated description.
    const summary =
      getAiDescriptionResponse(story)?.response?.toString().trim() ?? '';
    const contentItems = getContentItems({
      clips: clipCount,
      publishedClips: publishedClipCount,
      quotes: pullQuotesCount,
      articles: articlesCount,
      social: socialPostsCount,
    });

    const handleCheckboxClick = (event: MouseEvent<HTMLLabelElement>) => {
      // Prevent the row's single-click handler from firing when the checkbox is toggled
      event.stopPropagation();
    };

    // Shared by both row shapes: an uploading / failed story is still a story
    // the user needs to be able to bulk-select and delete, so its status row
    // gets the same checkbox as a finished one.
    const selectCheckbox = (
      <CheckboxLabel onClick={handleCheckboxClick}>
        <Checkbox
          type="checkbox"
          aria-label={`Select ${story.title}`}
          checked={isBulkSelected}
          onChange={() => selection.toggle(story.id)}
          data-testid={`unfoldered-story-select-${story.id}`}
        />
      </CheckboxLabel>
    );

    const handleDragStart = (event: DragEvent<HTMLDivElement>) => {
      // If the dragged row is part of the active selection, drag the whole
      // selection; otherwise drag just this row's id (matches OS file-manager
      // behaviour). `isBulkSelected` implies size >= 1, so no extra size check.
      const ids = isBulkSelected
        ? Array.from(selection.selectedIds)
        : [story.id];
      setStoryIdsDragPayload(event.dataTransfer, ids);
      setStoriesDragImage(event.dataTransfer, ids.length);
      setIsDragging(true);
    };

    const handleDragEnd = () => setIsDragging(false);

    // While the story is uploading/processing (or failed), show a compact,
    // non-interactive status line item instead of the normal clickable row.
    // Once the transcript is ready the normal interactive row takes over even
    // though video work continues (PLAT-11224).
    if ((isLoading && !canEnterEditor) || isFailedOrAbandoned) {
      return (
        <Row
          as="div"
          data-static
          data-selected={isBulkSelected || undefined}
          data-testid={`unfoldered-story-row-${story.id}`}
        >
          <Left>
            {/* Empty arrow slot so the status thumb lines up with the
                thumbnails on normal (interactive) rows below. */}
            <ArrowSpacer aria-hidden="true" />
            {selectCheckbox}
            <StatusThumb data-error={isFailedOrAbandoned || undefined}>
              {isLoading ? (
                <Spinner aria-label="Uploading" />
              ) : (
                <FailedGlyph aria-hidden="true">!</FailedGlyph>
              )}
            </StatusThumb>
            <StatusInfo>
              <Title>{story.title}</Title>
              {isLoading ? (
                <ProgressRow>
                  <ProgressTrack
                    role="progressbar"
                    aria-valuenow={displayPercent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <ProgressFill style={{ width: `${displayPercent}%` }} />
                  </ProgressTrack>
                  <StatusText>
                    {`${stepInfo.label}${
                      stepInfo.done
                        ? ''
                        : ` (${stepInfo.stepNumber}/${stepInfo.totalSteps})`
                    } · ${displayPercent}%`}
                  </StatusText>
                </ProgressRow>
              ) : (
                <FailedRow>
                  <StatusText data-error>Upload incomplete</StatusText>
                  {!isProcessingFailure(story) && (
                    <FailedActions>
                      {/* PLAT-11227: failed-partial (transcript already
                          editable) gets no delete-and-reupload retry — it
                          would destroy the user's edits. */}
                      {!isStoryTranscriptionReady(story) && (
                        <RetryButton
                          onClick={event => {
                            event.stopPropagation();
                            onRetry(story);
                          }}
                        >
                          Retry
                        </RetryButton>
                      )}
                      <RemoveButton
                        onClick={event => {
                          event.stopPropagation();
                          onRemoveFailed(story);
                        }}
                      >
                        Remove
                      </RemoveButton>
                    </FailedActions>
                  )}
                </FailedRow>
              )}
            </StatusInfo>
          </Left>
        </Row>
      );
    }

    return (
      <>
        <Row
          $selected={isRowSelected}
          $dragging={isDragging}
          onClick={event => {
            event.stopPropagation();
            onSelect?.(story);
            onOpen(story);
          }}
          draggable
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          data-selected={isBulkSelected || undefined}
          data-testid={`unfoldered-story-row-${story.id}`}
        >
          <Left>
            <ExpandArrow
              type="button"
              $hasClips={hasClips}
              $expanded={!!isExpanded}
              onClick={event => {
                if (!hasClips) return;
                event.stopPropagation();
                onToggleExpand?.(story.id, !isExpanded);
              }}
              aria-expanded={hasClips ? !!isExpanded : undefined}
              aria-label={isExpanded ? 'Collapse clips' : 'Expand clips'}
            >
              {hasClips && <ChevronDownIcon width="12" height="12" />}
            </ExpandArrow>
            {selectCheckbox}
            <Thumb>
              {thumbnail ? (
                <ThumbImg src={thumbnail} alt="" />
              ) : (
                <ThumbFallback />
              )}
            </Thumb>
            <TitleBlock>
              <Title>{story.title}</Title>
              {summary && <Summary>{summary}</Summary>}
            </TitleBlock>
          </Left>
          <Right data-cols="true">
            <DurationCol title="Duration">
              {duration && (
                <>
                  <ClockIcon />
                  <span>{duration}</span>
                </>
              )}
            </DurationCol>
            <ContentCell items={contentItems} />
            <TopicsCol title="Topics">
              <StoryTopicsCell topics={topicTitles} storyId={story.id} />
            </TopicsCol>
            <DateCol>{date ?? ''}</DateCol>
            <StoryRowActionsMenu
              onRename={() => onRename(story)}
              onDelete={() => onDelete(story)}
              onMove={() => actions.openMoveDialog([story.id])}
              onDetails={() => onDetails?.(story)}
            />
          </Right>
        </Row>
        {isExpanded &&
          clips.map((clip, index) => (
            <StoryClipRow
              key={clip.id}
              clip={clip}
              storyThumbnailUrl={thumbnail}
              isSelected={selectedClipId === clip.id}
              isLast={index === clips.length - 1}
              onSelect={onSelectClip}
              onOpen={onOpenClip}
              onDetails={onClipDetails}
            />
          ))}
      </>
    );
  },
);

export default UnfolderedStoryRow;

const Row = styled.div<{ $selected?: boolean; $dragging?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-lg);
  /* Taller row (~+20%) so the two-line summary beneath the title has room. */
  padding: 18px var(--spacing-md);
  border-radius: 0;
  background: transparent;
  border-bottom: 1px solid
    color-mix(in srgb, var(--text-muted) 40%, transparent);
  cursor: pointer;
  transition:
    background 120ms ease,
    box-shadow 120ms ease,
    opacity 120ms ease;
  ${({ $selected }) => $selected && selectedRowIndicator}
  ${({ $dragging }) => $dragging && draggingSource}

  &:hover {
    background: ${({ $selected }) =>
      $selected ? 'var(--surface-elevated)' : 'var(--surface)'};
  }

  /* bulk-selection checkbox active — subtle tint separate from row highlight */
  &[data-selected] {
    background: var(--surface);
  }

  /* Non-interactive status rows (uploading / failed) don't react to hover. */
  &[data-static] {
    cursor: default;
  }

  /* :not([data-selected]) so hovering a checked status row keeps the
     selection tint — [data-static]:hover would otherwise out-specify it. */
  &[data-static]:not([data-selected]):hover {
    background: transparent;
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const StatusThumb = styled.div`
  width: 102px;
  height: 58px;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface);
  border: 1px solid var(--border);

  &[data-error] {
    border-color: var(--destructive);
  }
`;

const Spinner = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid var(--border);
  border-top-color: var(--primary);
  animation: ${spin} 0.8s linear infinite;
`;

const FailedGlyph = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-bold);
  line-height: 1;
  color: var(--destructive);
`;

const StatusInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  min-width: 0;
`;

const ProgressTrack = styled.div`
  width: 140px;
  flex-shrink: 0;
  height: 3px;
  border-radius: 999px;
  background: var(--border);
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: var(--primary);
  transition: width 200ms ease;
`;

const StatusText = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  white-space: nowrap;

  &[data-error] {
    color: var(--destructive);
  }
`;

// Holds the "Upload incomplete" text alongside the Retry/Remove actions for a
// failed upload (mirrors the recent view's FailedActions affordance).
const FailedRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  min-width: 0;
`;

const FailedActions = styled.div`
  display: flex;
  gap: var(--spacing-xs);
  flex-shrink: 0;
`;

const ActionButton = styled.button`
  border: none;
  border-radius: 4px;
  padding: var(--spacing-2xs) var(--spacing-sm);
  font-size: 11px;
  font-weight: var(--font-weight-semi-bold);
  cursor: pointer;
  line-height: 1.4;
`;

const RetryButton = styled(ActionButton)`
  background-color: var(--primary);
  color: var(--background);

  &:hover {
    background-color: var(--primary-hover);
  }
`;

const RemoveButton = styled(ActionButton)`
  background-color: var(--destructive);
  color: var(--foreground);

  &:hover {
    background-color: color-mix(in srgb, var(--destructive) 85%, black);
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  min-width: 0;
  flex: 1;
`;

/*
 * The clips disclosure. A real <button>, so it toggles from the keyboard and
 * carries `aria-expanded`, and its hit area is far wider than the 12px glyph:
 * the row itself navigates into the story on click, so a near-miss here doesn't
 * just do nothing — it takes the user out of the Story Manager entirely.
 */
const ExpandArrow = styled.button<{ $hasClips: boolean; $expanded: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  padding: 0;
  border: none;
  background: none;
  flex-shrink: 0;
  color: var(--text-muted);
  cursor: ${({ $hasClips }) => ($hasClips ? 'pointer' : 'default')};
  opacity: ${({ $hasClips }) => ($hasClips ? 1 : 0)};
  /* A clip-less story keeps an invisible glyph for alignment; let its clicks
     reach the row, exactly as before the hit area grew. */
  pointer-events: ${({ $hasClips }) => ($hasClips ? 'auto' : 'none')};

  /*
   * 34x40 target around a 14px box — well past the 24px minimum without moving
   * anything: the layout footprint stays 14px and only this overlay grows.
   * Asymmetric because the space around it is: left runs 14px into the row's own
   * 16px padding (dead space, 2px shy of the edge), right stops 2px short of the
   * 8px gap to the checkbox so the two controls never trade clicks, and the
   * vertical 13px sits inside the row's 18px padding.
   */
  &::after {
    content: '';
    position: absolute;
    top: -13px;
    bottom: -13px;
    left: -14px;
    right: -6px;
  }

  /* Chevron points down when expanded, right when collapsed. */
  & > svg {
    transition: transform 150ms ease;
    transform: rotate(${({ $expanded }) => ($expanded ? '0deg' : '-90deg')});
  }

  &:hover {
    color: ${({ $hasClips }) =>
      $hasClips ? 'var(--text-primary)' : 'var(--text-muted)'};
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;

/* Invisible stand-in matching ExpandArrow's 14px footprint, so status rows
   align with interactive rows. Not an ExpandArrow with no clips: an
   `aria-hidden` <button> would still take focus. */
const ArrowSpacer = styled.span`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`;

const CheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  padding: 2px;
`;

const Checkbox = styled.input`
  appearance: none;
  width: 16px;
  height: 16px;
  margin: 0;
  border: 1px solid var(--border);
  border-radius: 3px;
  background: var(--surface);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    background 120ms ease,
    border-color 120ms ease;

  &:hover {
    border-color: var(--text-secondary);
  }

  &:checked {
    background: var(--accent);
    border-color: var(--accent);
  }

  &:checked::after {
    content: '';
    width: 4px;
    height: 8px;
    border: solid var(--background);
    border-width: 0 2px 2px 0;
    transform: rotate(45deg) translate(-1px, -1px);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
`;

const Thumb = styled.div`
  width: 102px;
  height: 58px;
  border-radius: 8px;
  overflow: hidden;
  background: var(--border);
  flex-shrink: 0;
`;

const ThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ThumbFallback = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, var(--border) 0%, var(--surface) 100%);
`;

// Title + summary stack vertically so the summary reads as a sub-line of the
// title. min-width: 0 lets both the title ellipsis and the summary clamp work.
const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const Title = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Secondary, supporting context: smaller + dimmer than the title, clamped to
// two lines (mirrors the recent view's StoryDescription).
const Summary = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
  color: var(--text-secondary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  flex-shrink: 0;
`;

const Stat = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  color: var(--text-secondary);

  svg {
    color: var(--text-muted);
  }
`;

const DurationCol = styled(Stat)`
  width: 72px;
  justify-content: flex-start;
`;

// Holds the topic chips (two per line) + the "+N" overflow indicator.
const TopicsCol = styled(Stat)`
  width: 215px;
  justify-content: flex-start;
  overflow: hidden;
`;

const DateCol = styled.span`
  width: 88px;
  text-align: right;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  color: var(--text-secondary);
  white-space: nowrap;
`;
